<template>
    <div>
        <va-card class="overflow-auto">
            <vue-element-loading :active="visible" :is-full-screen="true" />

            <div class="row mb-3">
                <div class="col-md-8">
                    <h4 style="font-size: 24px; font-weight: bold">
                        Offender Details: {{ offender.name }}
                    </h4>
                </div>
                <div class="col-md-4 text-right">
                    <b-button variant="secondary" @click="$router.go(-1)">
                        <i class="fa fa-arrow-left mr-2"></i>Back
                    </b-button>
                    <b-button variant="warning" class="ml-2" @click="editOffender">
                        <i class="fa fa-edit mr-2"></i>Edit
                    </b-button>
                </div>
            </div>

            <div v-if="offender" class="row">
                <!-- Basic Information -->
                <div class="col-md-6">
                    <div class="card">
                        <div class="card-header bg-primary text-white">
                            <h5 class="mb-0">Basic Information</h5>
                        </div>
                        <div class="card-body">
                            <table class="table table-borderless">
                                <tr>
                                    <td><strong>Name:</strong></td>
                                    <td>{{ offender.name }}</td>
                                </tr>
                                <tr>
                                    <td><strong>Alias:</strong></td>
                                    <td>{{ offender.alias || 'N/A' }}</td>
                                </tr>
                                <tr>
                                    <td><strong>Age:</strong></td>
                                    <td>{{ offender.age }} years</td>
                                </tr>
                                <tr>
                                    <td><strong>Gender:</strong></td>
                                    <td>{{ offender.sex }}</td>
                                </tr>
                                <tr>
                                    <td><strong>Date of Birth:</strong></td>
                                    <td>{{ formatDate(offender.dob) }}</td>
                                </tr>
                                <tr>
                                    <td><strong>Father's Name:</strong></td>
                                    <td>{{ offender.fathersName || 'N/A' }}</td>
                                </tr>
                                <tr>
                                    <td><strong>Occupation:</strong></td>
                                    <td>{{ offender.occupation || 'N/A' }}</td>
                                </tr>
                                <tr>
                                    <td><strong>Address:</strong></td>
                                    <td>{{ offender.address }}</td>
                                </tr>
                            </table>
                        </div>
                    </div>
                </div>

                <!-- Contact Information -->
                <div class="col-md-6">
                    <div class="card">
                        <div class="card-header bg-info text-white">
                            <h5 class="mb-0">Contact Information</h5>
                        </div>
                        <div class="card-body">
                            <table class="table table-borderless">
                                <tr>
                                    <td><strong>Contact Number:</strong></td>
                                    <td>{{ offender.contactNo || 'N/A' }}</td>
                                </tr>
                                <tr>
                                    <td><strong>Aadhar Number:</strong></td>
                                    <td>{{ offender.adharNo || 'N/A' }}</td>
                                </tr>
                                <tr>
                                    <td><strong>Phone Numbers:</strong></td>
                                    <td>
                                        <span v-if="offender.phone && offender.phone.length">
                                            <span v-for="phone in offender.phone" :key="phone._id"
                                                class="badge badge-secondary mr-1">
                                                {{ phone.mobile || phone }}
                                            </span>
                                        </span>
                                        <span v-else>N/A</span>
                                    </td>
                                </tr>
                            </table>
                        </div>
                    </div>
                </div>

                <!-- Physical Description -->
                <div class="col-md-6 mt-3">
                    <div class="card">
                        <div class="card-header bg-success text-white">
                            <h5 class="mb-0">Physical Description</h5>
                        </div>
                        <div class="card-body">
                            <table class="table table-borderless">
                                <tr>
                                    <td><strong>Height:</strong></td>
                                    <td>{{ offender.height ? offender.height + ' cm' : 'N/A' }}</td>
                                </tr>
                                <tr>
                                    <td><strong>Complexion:</strong></td>
                                    <td>{{ offender.complexion || 'N/A' }}</td>
                                </tr>
                                <tr>
                                    <td><strong>Face:</strong></td>
                                    <td>{{ offender.face || 'N/A' }}</td>
                                </tr>
                                <tr>
                                    <td><strong>Hair:</strong></td>
                                    <td>{{ offender.hair || 'N/A' }}</td>
                                </tr>
                                <tr>
                                    <td><strong>Body Build:</strong></td>
                                    <td>{{ offender.bodybuild || 'N/A' }}</td>
                                </tr>
                                <tr>
                                    <td><strong>Eyes:</strong></td>
                                    <td>{{ offender.eyes || 'N/A' }}</td>
                                </tr>
                                <!-- <tr>
                                    <td><strong>Identification Mark:</strong></td>
                                    <td>{{ offender.identificationMark || 'N/A' }}</td>
                                </tr> -->
                                <tr>
                                    <td><strong>Prominent Marks:</strong></td>
                                    <td>
                                        <span
                                            v-if="offender.prominentIdentificationMarks && offender.prominentIdentificationMarks.length">
                                            <span v-for="mark in offender.prominentIdentificationMarks" :key="mark._id"
                                                class="badge badge-warning mr-1">
                                                {{ mark.marktype }}: {{ mark.details }}
                                            </span>
                                        </span>
                                        <span v-else>N/A</span>
                                    </td>
                                </tr>
                            </table>
                        </div>
                    </div>
                </div>

                <!-- Criminal Information -->
                <div class="col-md-6 mt-3">
                    <div class="card">
                        <div class="card-header bg-danger text-white">
                            <h5 class="mb-0">Criminal Information</h5>
                        </div>
                        <div class="card-body">
                            <table class="table table-borderless">
                                <tr>
                                    <td><strong>Offences:</strong></td>
                                    <td><span class="badge badge-primary">{{ offender.offences }}</span></td>
                                </tr>
                                <tr>
                                    <td><strong>Threat Level:</strong></td>
                                    <td>
                                        <span :class="getThreatLevelClass(offender.threatLevel)" class="badge">
                                            {{ offender.threatLevel }}
                                        </span>
                                    </td>
                                </tr>
                                <tr>
                                    <td><strong>Activity Level:</strong></td>
                                    <td>
                                        <span :class="getActivityLevelClass(offender.activityLevel)" class="badge">
                                            {{ offender.activityLevel }}
                                        </span>
                                    </td>
                                </tr>
                                <tr>
                                    <td><strong>Expertise:</strong></td>
                                    <td>
                                        <span v-if="offender.Expertise && offender.Expertise.length">
                                            <span v-for="expertise in offender.Expertise" :key="expertise"
                                                class="badge badge-info mr-1">
                                                {{ expertise }}
                                            </span>
                                        </span>
                                        <span v-else>N/A</span>
                                    </td>
                                </tr>
                                <tr>
                                    <td><strong>Habits:</strong></td>
                                    <td>
                                        <span v-if="offender.habits && offender.habits.length">
                                            <span v-for="habit in offender.habits" :key="habit"
                                                class="badge badge-secondary mr-1">
                                                {{ habit }}
                                            </span>
                                        </span>
                                        <span v-else>N/A</span>
                                    </td>
                                </tr>
                                <tr>
                                    <td><strong>Vehicles Owned:</strong></td>
                                    <td>
                                        <span v-if="offender.vehicleOwned && offender.vehicleOwned.length">
                                            <span v-for="vehicle in offender.vehicleOwned" :key="vehicle._id"
                                                class="badge badge-success mr-1">
                                                {{ vehicle.brand }} {{ vehicle.model }} ({{ vehicle.regno }}) - {{
                                                    vehicle.color }}
                                            </span>
                                        </span>
                                        <span v-else>N/A</span>
                                    </td>
                                </tr>
                                <tr>
                                    <td><strong>Guns/Weapons:</strong></td>
                                    <td>
                                        <span v-if="offender.gun && offender.gun.length">
                                            <span v-for="weapon in offender.gun" :key="weapon._id"
                                                class="badge badge-danger mr-1">
                                                {{ weapon.guntype }} ({{ weapon.gunno }})
                                            </span>
                                        </span>
                                        <span v-else>N/A</span>
                                    </td>
                                </tr>
                            </table>
                        </div>
                    </div>
                </div>

                <!-- Crime History -->
                <div class="col-md-12 mt-3">
                    <div class="card">
                        <div class="card-header bg-warning text-dark">
                            <h5 class="mb-0">Crime History & Police Records</h5>
                        </div>
                        <div class="card-body">
                            <p v-if="offender.CrimeHistoryPoliceAccused">{{ offender.CrimeHistoryPoliceAccused }}</p>
                            <p v-else class="text-muted">No crime history recorded</p>
                        </div>
                    </div>
                </div>

                <!-- Family Details -->
                <div class="col-md-6 mt-3">
                    <div class="card">
                        <div class="card-header bg-secondary text-white">
                            <h5 class="mb-0">Family Details</h5>
                        </div>
                        <div class="card-body">
                            <div v-if="offender.familyDetails && Object.keys(offender.familyDetails).length">
                                <div v-for="(value, key) in offender.familyDetails" :key="key" class="mb-2">
                                    <strong>{{ key.charAt(0).toUpperCase() + key.slice(1) }}:</strong>
                                    <span v-if="Array.isArray(value)">
                                        <span v-for="item in value" :key="item._id || item"
                                            class="badge badge-light mr-1">
                                            {{ item.name || item }}
                                        </span>
                                    </span>
                                    <span v-else>{{ value.name || value }}</span>
                                </div>
                            </div>
                            <p v-else class="text-muted">No family details recorded</p>
                        </div>
                    </div>
                </div>

                <!-- Documents & Images -->
                <div class="col-md-6 mt-3">
                    <div class="card">
                        <div class="card-header bg-dark text-white">
                            <h5 class="mb-0">Documents & Images</h5>
                        </div>
                        <div class="card-body">
                            <div v-if="offender.idproofs && offender.idproofs.length" class="mb-3">
                                <h6>ID Proofs:</h6>
                                <div v-for="(proof, index) in offender.idproofs" :key="index" class="mb-2">
                                    <small class="text-muted">{{ proof.idType }}: {{ proof.idNumber }}</small>
                                    <br>
                                    <img v-if="proof.image" 
                                        :src="`http://3.109.132.76/document/get/${proof.image}`"
                                        class="img-thumbnail clickable-image" 
                                        style="max-width: 100px; max-height: 100px; cursor: pointer;"
                                        @error="handleImageError"
                                        @click="openImageModal(`http://3.109.132.76/document/get/${proof.image}`, `${proof.idType} - ${proof.idNumber}`)" />
                                    <br>
                                </div>
                            </div>

                            <div v-if="offender.image && offender.image.length" class="mb-3">
                                <h6>General Images:</h6>
                                <div class="row">
                                    <div v-for="(img, index) in offender.image" :key="index" class="col-md-4 mb-2">
                                        <img :src="`http://3.109.132.76/document/get/${img.path}`" 
                                            class="img-thumbnail clickable-image"
                                            style="max-width: 100px; max-height: 100px; cursor: pointer;" 
                                            @error="handleImageError"
                                            @click="openImageModal(`http://3.109.132.76/document/get/${img.path}`, `General Image ${index + 1}`)" />
                                        <br>
                                    </div>
                                </div>
                            </div>

                            <div v-if="offender.leftThumb" class="mb-3">
                                <h6>Left Thumb Impression:</h6>
                                <img :src="`http://3.109.132.76/document/get/${offender.leftThumb}`"
                                    class="img-thumbnail clickable-image" 
                                    style="max-width: 100px; max-height: 100px; cursor: pointer;"
                                    @error="handleImageError"
                                    @click="openImageModal(`http://3.109.132.76/document/get/${offender.leftThumb}`, 'Left Thumb Impression')" />
                                <br>
                            </div>

                            <div v-if="offender.signature" class="mb-3">
                                <h6 class="text-sm font-semibold text-gray-700 mb-1">Signature</h6>
                                <img :src="`http://3.109.132.76/document/get/${offender.signature}`"
                                    class="img-thumbnail clickable-image" 
                                    style="max-width: 100px; max-height: 100px; cursor: pointer;"
                                    @error="handleImageError"
                                    @click="openImageModal(`http://3.109.132.76/document/get/${offender.signature}`, 'Signature')" />
                            </div>

                            <div
                                v-if="(!offender.idproofs || !offender.idproofs.length) && (!offender.image || !offender.image.length) && !offender.leftThumb">
                                <p class="text-muted">No documents or images uploaded</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else class="text-center py-4">
                <p>Loading offender details...</p>
            </div>
        </va-card>

        <!-- Image Modal -->
        <b-modal v-model="imageModal.show" 
                 :title="imageModal.title" 
                 size="lg" 
                 centered
                 hide-footer
                 @hide="closeImageModal">
            <div class="text-center">
                <img :src="imageModal.src" 
                     class="img-fluid" 
                     style="max-height: 70vh;"
                     @error="handleModalImageError" />
            </div>
        </b-modal>
    </div>
</template>

<script>
import axios from "axios";
import VueElementLoading from "vue-element-loading";

export default {
    name: "OffenderDetails",
    components: {
        VueElementLoading
    },
    data() {
        return {
            visible: false,
            offender: null,
            imageModal: {
                show: false,
                src: '',
                title: ''
            }
        };
    },
    async mounted() {
        await this.fetchOffenderDetails();
    },
    methods: {
        async fetchOffenderDetails() {
            this.visible = true;
            try {
                const offenderId = this.$route.query.id;
                if (!offenderId) {
                    this.$toast.error('Offender ID not provided');
                    this.$router.push('/offenders');
                    return;
                }

                const response = await axios.get(`/habitual/${offenderId}`);
                if (response.data.status) {
                    this.offender = response.data.data;
                } else {
                    this.$toast.error('Offender not found');
                    this.$router.push('/offenders');
                }
            } catch (error) {
                console.error('Error fetching offender details:', error);
                this.$toast.error('Failed to fetch offender details');
                this.$router.push('/offenders');
            } finally {
                this.visible = false;
            }
        },

        formatDate(dateString) {
            if (!dateString) return 'N/A';
            return new Date(dateString).toLocaleDateString();
        },

        getThreatLevelClass(level) {
            switch (level) {
                case 'High': return 'badge-danger';
                case 'Medium': return 'badge-warning';
                case 'Low': return 'badge-success';
                default: return 'badge-secondary';
            }
        },

        getActivityLevelClass(level) {
            switch (level) {
                case 'Active': return 'badge-danger';
                case 'Inactive': return 'badge-success';
                case 'Unknown': return 'badge-secondary';
                default: return 'badge-secondary';
            }
        },

        handleImageError(event) {
            // Don't hide the image, just show a placeholder or error message
            event.target.style.display = 'inline-block';
            event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjRjVGNUY1Ii8+Cjx0ZXh0IHg9IjUwIiB5PSI1MCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+SW1hZ2U8L3RleHQ+Cjx0ZXh0IHg9IjUwIiB5PSI2NSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjEwIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+Tm90IEZvdW5kPC90ZXh0Pgo8L3N2Zz4K';
        },

        openImageModal(src, title) {
            this.imageModal.src = src;
            this.imageModal.title = title;
            this.imageModal.show = true;
        },

        closeImageModal() {
            this.imageModal.show = false;
            this.imageModal.src = '';
            this.imageModal.title = '';
        },

        handleModalImageError(event) {
            event.target.style.display = 'inline-block';
            event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjRjVGNUY1Ii8+Cjx0ZXh0IHg9IjUwIiB5PSI1MCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+SW1hZ2U8L3RleHQ+Cjx0ZXh0IHg9IjUwIiB5PSI2NSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjEwIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+Tm90IEZvdW5kPC90ZXh0Pgo8L3N2Zz4K';
        },

        editOffender() {
            this.$router.push({
                path: '/edit-offender',
                query: { id: this.offender._id }
            });
        }
    }
};
</script>

<style scoped>
.card {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.card-header {
    font-weight: 600;
}

.table td {
    padding: 0.5rem 0;
    border: none;
}

.badge {
    font-size: 0.8em;
    padding: 4px 8px;
}

.img-thumbnail {
    border: 1px solid #ddd;
    border-radius: 4px;
}

.clickable-image {
    cursor: pointer;
}
</style>
